import { useSelector } from 'react-redux';

import { ThemeProvider, useTheme } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider, Snackbar, Alert } from '@mui/material';

// routing
import Routes from 'routes';

// defaultTheme
import themes from 'themes';

// project imports
import NavigationScroll from 'layout/NavigationScroll';
import { useAppSelector, useAppDispatch } from 'store';

import { notificationClear } from './store/slices/notificationSlice';
import { useEffect, useRef, useState } from 'react';
import { logout } from 'store/thunk/authThunk';
import { setUserData } from 'store/slices/authSlice';
import { useNavigate } from 'react-router';
import KeywordTracking from 'ui-component/popup/KeywordTracking';
import MetricsShutdown from 'ui-component/popup/MetricsShutdown';
import { setShowKeywordTrackingRelease } from './store/slices/dataLoadingSlice';
import { useLocation } from 'react-router-dom';
import AddStoreModalNew from 'views/manageStores/AddStoreModalNew';
// ==============================|| APP ||============================== //

const App = () => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const notificationInfo = useAppSelector((state) => state.notification);
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  const inactivityInterval = 55 * 60 * 1000;
  const initialDelay = 5 * 60 * 1000;
  const userActivityTimeout = useRef(null);
  const localUser = localStorage.getItem('user_data');
  const dataLoading = useAppSelector((state) => state.dataLoading);
  const userDetails = useAppSelector((state) => state.authorization.userDetails);
  const [forceStorePopup, setForceStorePopup] = useState(false);

  const handleLogout = async () => {
    appDispatch(logout());
    appDispatch(setUserData(null));

    localStorage.removeItem('user_data');
    localStorage.removeItem('sortActions');
    localStorage.removeItem('visibleColumns');
    localStorage.removeItem('selectedDates');
    localStorage.removeItem('sortActions_cogs');
    localStorage.removeItem('selectedTab');
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    }
    navigate('/login');
  };

  useEffect(() => {
    const handleInactivity = () => {
      clearInterval(userActivityTimeout.current);

      userActivityTimeout.current = setTimeout(() => {
        if (localUser) {
          userActivityTimeout.current = setInterval(() => {
            handleLogout();
          }, inactivityInterval);
        }
      }, initialDelay);
    };

    window.addEventListener('mousemove', handleInactivity);
    window.addEventListener('keydown', handleInactivity);

    // Clean up the interval on component unmount
    return () => {
      clearTimeout(userActivityTimeout.current);
      clearInterval(userActivityTimeout.current);
      window.removeEventListener('mousemove', handleInactivity);
      window.removeEventListener('keydown', handleInactivity);
    };
    // }
  }, [handleLogout, initialDelay, inactivityInterval]);

  useEffect(() => {
    const container = document.body;
    container.style.zoom = '100%';
    return () => {
      container.style.zoom = '80%';
    };
  }, []);

  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    if (
      !userDetails?.data?.is_store_setup &&
      pathname !== '/login' &&
      pathname !== '/register' &&
      pathname !== '/forgot-password' &&
      pathname !== '/verify-account' &&
      pathname !== '/reset-password'
    ) {
      navigate('/account-settings');
    }
  }, [pathname]);
  const userData = JSON.parse(localStorage.getItem('user_data'));
  useEffect(() => {
    if (userData && userData?.data && !userData?.data?.is_store_setup && userData?.data?.is_subscribed) {
      const timer = setTimeout(() => {
        setForceStorePopup(true);
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [userData, userDetails]);

  const clearNotification = () => {
    appDispatch(notificationClear());
  };

  const openNotification = () => {
    return notificationInfo.message ? true : false;
  };

  const onKeywordTrackingClose = () => {
    appDispatch(setShowKeywordTrackingRelease(false));
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes(customization)}>
        {localUser && <MetricsShutdown />}
        {forceStorePopup && <AddStoreModalNew forceStorePopup={forceStorePopup} setForceStorePopup={setForceStorePopup} />}
        <CssBaseline />
        {notificationInfo?.message && (
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            className="notification-snackbar"
            open={openNotification()}
            onClose={clearNotification}
            autoHideDuration={notificationInfo.status ? 2500 : 10000}
          >
            <Alert
              className="notification-alert"
              onClose={clearNotification}
              severity={notificationInfo.status ? 'success' : 'error'}
              sx={{
                width: '100%',
                color: theme.palette.background.default,
                bgcolor: notificationInfo.status ? theme.palette.success.main : theme.palette.error.main
              }}
              variant="filled"
            >
              {notificationInfo.message}
            </Alert>
          </Snackbar>
        )}
        {dataLoading.showKeywordTrackingRelease ? <KeywordTracking open={true} handleClose={onKeywordTrackingClose} /> : null}
        <NavigationScroll>
          <Routes />
        </NavigationScroll>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
